<template>
    <div class="full-width address-chooser" role="group" aria-label="address settings">
        <div v-if="customer.addresses.length">
            <div class="full-width" v-for="(address, index) in sortedAddresses"  :key="index">
				<CheckoutChoice
					:class="{'tooltip is-tooltip-warning': !address.delivery.isDeliverable}"
					class="address"
					:data-tooltip="addressTooltip(address)"
					v-if="showAddress(address)"
					:isActive="isActive(address)"
					:isDisabled="!address.delivery.isDeliverable"
					:ariaLabel="`address: ${address.formattedAddress}`"
					:clickHandler="() => {setAddress(address)}">
					<!-- address text -->
					<span :class="{'store-navigation-color is-bolder': isActive(address)}">
						<template v-if="$mq !== 'mobile'">
								{{formatAddress(address)}}
								<p class="is-size-7 has-text-grey" v-if="address.note">{{address.note}}</p>
						</template>
						<template v-else>
							{{address.formattedAddress.substr(0, address.formattedAddress.indexOf(',')) || address.formattedAddress}}
						</template>
					</span>
					<!-- edit, delete -->
					<span class="align-right" v-if="!$route.meta.isCheckout">
						<a href="/?edit-address" id="edit-address" @click.prevent="toggleAddressForm(API_CMDS.ADDRESS.EDIT, address)">Edit</a>
						<a href="/?delete-address" @click.prevent="deleteAddress(address.id)">Delete</a>
					</span>
				</CheckoutChoice>
			</div>
		</div>
		<div class="full-width is-inline-block section-actions" v-if="displayAddAddress()">
			<a
				:href="showAllAddressLink"
				v-if="1 < customer.addresses.length"
				@click.prevent="showAllAddresses = !showAllAddresses"
			>
				{{(showAllAddresses) ? 'Hide' : 'Show'}} all
			</a>
			<a
				:href="newAddressLink"
				class="is-pulled-right address-functionality"
				@click.prevent="toggleAddressForm(API_CMDS.ADDRESS.ADD)"
			>
				{{(!addAddressFlag) ? 'Add new' : 'Hide'}}
			</a>
        </div>
        <div v-if="addAddressFlag || !displayAddAddress()" 
			class="gmap-autocomplete">
            <div class="field">
                <!-- full address -->
                <div class="control" @keyup="updateAutoCompleteText">
                    <GmapAutocomplete v-if="getGmapAPIKey"
                        @place_changed="setPlace"
                        :placeholder="getAddressForGmap"
                        class="input is-medium is-hovered is-size-6 text-input auto-complete-field"
                        type="text" >
                    </GmapAutocomplete>
					<input v-if="!getGmapAPIKey"
						id="address-field"
						type="text"
						class="input is-medium is-hovered is-size-6 text-input auto-complete-field"
						placeholder="Enter Address"
						@blur="addAddressFromField"/>
                </div>
            </div>

            <div id="apt" class="field">
                <!-- apartment section -->
                <div class="control">
                    <div class="columns is-mobile is-gapless">
                        <div class="column is-narrow">
                            <input v-model="newAddress.aptNum" class="input text-input" type="text" placeholder="Apt #">
                        </div>
                        <!-- notes -->
                        <div class="column">
                            <input v-model="newAddress.note" class="input text-input" type="text" placeholder="Notes, Instructions, etc.">
                        </div>
                    </div>
                </div>
            </div>
            <BaseButton
                :classNames="['button--modifier']"
                :title="(newAddress.cmd === 'add') ? 'Add Address' : 'Edit Address'"
                @click="addNewAddress"
            />
            {{msg}}
        </div>
    </div>
</template>

<script>
import EventBus from '@EventBus';
import { ACCOUNT_CHECKOUT, ACCOUNT, LOCATION } from "@/stores/defs";
import { API_CMDS } from "@/common/messages/api.cmds";
import { ERROR_MSG } from "@/common/messages/error.msg";
import { mapGetters, mapActions } from "vuex";
import { SUCCESS_MSG } from "@/common/messages/success.msg";
import AddressModel from "@/models/AddressModel";
import addressService from "@/services/address.service";
import CheckoutChoice from '@/components/checkout/CheckoutChoice';
import customerService from "@/services/customer.service";
import FormValidator from "@/utils/FormValidator";
import Formatter from '@/utils/Formatter';
import Analytics from '@Analytics';
import { ADDRESS_ANALYTIC_EVENT } from '@/common/analytic-events/addressAnalyticEvent';
import { generateFullAddress } from '@/utils/helper.functions';
import addressMixin from '@Mixin/addressMixin';
import mapsMixin from '@Mixin/mapsMixin';
import featureMixin from '../mixins/featureMixin';
import FEATURES from '../../utils/Features';

export default {
	mixins: [
		addressMixin,
		mapsMixin,
		featureMixin
	],
    components: {
        CheckoutChoice
    },
    computed: {
        ...mapGetters({
            customer: ACCOUNT.GET_CUSTOMER,
			deliveryAddress: ACCOUNT_CHECKOUT.GET_DELIVERY_ADDRESS,
			guestPhone: ACCOUNT.GET_GUEST_PHONE,
			isGuest: ACCOUNT_CHECKOUT.GET_IS_GUEST,
			guestId: ACCOUNT_CHECKOUT.GET_GUEST_ID,
            location: LOCATION.GET_LOCATION,
			loggedIn: ACCOUNT.GET_LOGGED_IN,
        }),
        sortedAddresses() {
            if (this.customer.addresses) {
                return this.customer.addresses.sort((a, b) => {
                   if ( (a.delivery.isDeliverable && b.delivery.isDeliverable) && (a.lastUsedDate && b.lastUsedDate)) {
                       return b.lastUsedDate - a.lastUsedDate
                   } 
                   return b.delivery.isDeliverable - a.delivery.isDeliverable;
                });
            } else {
                return [];
            }
        },
        chosenAddress() {
            return this.deliveryAddress ? this.deliveryAddress.id : -1;
		},
		newAddressLink() {
			return (!this.addAddressFlag) ? '/?add-new-address' : '/?hide-address';
		},
		showAllAddressLink() {
			return (this.showAllAddresses) ? '/?hide-all-addresses' : '/?show-all-addresses';
		},
		getAddressForGmap() {
			const newAddress = this.newAddress;
			if (newAddress.cmd === API_CMDS.ADDRESS.ADD) {
				return 'Enter Street Address';
			}
			if (!newAddress.address) {
				return;
			}
			const streetNumber = this.getStreetNumber(newAddress);
			const streetName = this.getStreetName(newAddress);
			return `${streetNumber} ${streetName}`;
		}
	},
    data() {
        return {
            API_CMDS: API_CMDS,
            msg: "",
            addAddressFlag: false,
            guestAddresses: [],
            fullName: "",
            addressType: "",
            newAddress: {
                address: null,
                addressComponents: {},
                base: null,
                cb: null,
                cmd: API_CMDS.ADDRESS.ADD,
                id: "",
                latitude: "",
                longitude: "",
                phone: "",
                name: "",
                aptNum: "",
                note: ""
            },
            showAllAddresses: false,
			addressLimit: 1,
        };
    },
    methods: {
        showAddress(address) {
			if (!address.base) {
				return false;
			}
            // only show delivery address if not collapsed
            if (!this.showAllAddresses) {
                return this.deliveryAddress && this.deliveryAddress.id === address.id
            } else {
                return true;
            }
        },
        formatAddress: Formatter.formatAddress,
        isActive(address) {
            return address.id === this.chosenAddress && address.delivery.isDeliverable
        },
        ...mapActions({
            setDeliveryAddress: ACCOUNT_CHECKOUT.SET_DELIVERY_ADDRESS,
            addAddressToCustomer: ACCOUNT.ADD_ADDRESS,
            deleteUsersAddress: ACCOUNT.DELETE_ADDRESS,
            editAddress: ACCOUNT.EDIT_ADDRESS,
            updateCustomer: ACCOUNT.UPDATE_CUSTOMER
        }),
        resetAddress(cmd) {
            this.newAddress = {
                address: null,
                aptNum: "",
                base: null,
                cb: null,
                cmd: cmd ? cmd : "",
                id: "",
                latitude: "",
                longitude: "",
                note: "",
                phone: ""
            };
        },
        paginateAddresses() {
            this.showAllAddresses = !this.showAllAddresses;
            this.addressLimit = this.showAllAddresses
                ? this.sortedAddresses.length
                : Math.min(1, this.sortedAddresses.length);
		},
		addressTooltip(address) {
			const isDeliverable = address.delivery.isDeliverable;
			if (!isDeliverable) {
				return 'Not deliverable to this location';
			}
			return '';
		},
		displayAddAddress() {
			const customer = this.customer;
			if (!customer) {
				return false;
			}
			const addressCount = customer.addresses.length;
			if (addressCount === 0) {
				return false;
			}
			return true;
		},
        addNewAddress() {
			const requireCoordinates = this.getGmapAPIKey ? true : false;
			const hasCoordinates = this.newAddress.latitude && this.newAddress.longitude;
			const skipCoordinates = (requireCoordinates) ? hasCoordinates : true;
            const response = this.newAddress.address && skipCoordinates;
			if (!response) {
				this.msg = ERROR_MSG.COMMON.MISSING_FIELDS;
				return;
			}
            if (this.newAddress.cmd === API_CMDS.ADDRESS.ADD) {
                delete this.newAddress.id;
			}
			const customer = this.customer;
			const phone = customer.phone || this.guestPhone;
			const customerId = customer.id || this.guestId;
			this.newAddress.phone = phone;
			this.newAddress.b = this.location.id;
			this.newAddress.cb = customerId;
			const aptNum = this.newAddress.aptNum;
			const address = this.newAddress.address;
			this.newAddress.address = generateFullAddress(address, aptNum);
			customerService.addDeliveryAddress(this.newAddress).then(res => {
				if (res.data.body.address) {
					const newAddress = new AddressModel(res.data.body.address);
					// assign isDeliverable if location doesn't already do it
					if (!this.location.delivery.hasDeliveryRegionsSet) {
						newAddress.delivery.isDeliverable = addressService.isWithinRangeOfLocation(
							newAddress,
							this.location
						);
					}
					this.msg = SUCCESS_MSG.ADDRESS.ADDED;

					// add to customers addresses
					if (this.newAddress.cmd === API_CMDS.ADDRESS.ADD) {
						this.addAddressToCustomer(newAddress);
					} else if (this.newAddress.cmd === API_CMDS.ADDRESS.EDIT) {
						this.editAddress(newAddress);
					}
					this.trackAddress(newAddress);


					if (this.checkForFeature(FEATURES.DELIVERY)) {
						// delivery address
						// check if in range before setting to delivery address
						const isDeliverable = newAddress.delivery.isDeliverable;
						if (isDeliverable) {
							this.setAddress(newAddress);
							this.$emit("success");
						} else {
							this.$emit(
								"error",
								ERROR_MSG.ADDRESS.MISSING_ADDRESS(
									this.location.delivery.deliveryRadius,
									this.location.delivery.deliveryRadiusUnit
								)
							);
						}
					} else {
						this.setAddress(newAddress);
						this.$emit("success");
					}

					this.addAddressFlag = false;
					this.resetAddress();
				} else {
					this.$emit("error", ERROR_MSG.ADDRESS.ADDING_ERR);
				}
			}).catch(err => {
				console.log(err);
			});
			if (this.isGuest) {
				const newAddress = new AddressModel({
					id: this.guestAddresses.length,
					address_id: this.guestAddresses.length,
					name: this.fullName,
					address: this.newAddress.address,
					latitude: this.newAddress.latitude,
					longitude: this.newAddress.longitude
				});
				this.guestAddresses.push(newAddress);
				const customer = this.customer;
				customer.addresses.push(newAddress);
				this.addAddressFlag = false;
				this.setAddress(this.guestAddresses[this.guestAddresses.length - 1]);
				this.trackAddress();
			} else {
				this.$emit("error", ERROR_MSG.ADDRESS.ADDING_ERR);
			}
        },
        trackAddress(address) {
			const addressEventLabel = this.getAddressEventLabel(address);
			this.$tpa.trackUserAddressAdd(addressEventLabel);
        },
        setAddress(address) {
            if (address.delivery.isDeliverable) {
				this.setDeliveryAddress(address);
				// TODO mixin
				const addressEventLabel = this.getAddressEventLabel(address);
				this.$tpa.trackUserAddressSet(addressEventLabel);
			}
		},
		getAddressEventLabel(address) {
			const useAddress = address || this.deliveryAddress;
			const addressName = useAddress.formattedAddress;
			const addressId = useAddress.id;
			const addressEventLabel = `${addressName} - ${addressId}`;
			return addressEventLabel;
		},
        setPlace(place) {
            this.addressType = place.types[0];
            const extractedGPlaceProps = addressService.extractGPLaceProps(place);
            if (extractedGPlaceProps) {
                this.msg = "";
                this.newAddress = {
                    ...this.newAddress,
                    address: place.formatted_address,
                    base: this.location.base || null,
                    cb: this.customer.id || null,
                    cmd: this.newAddress.cmd,
                    id: this.newAddress.id || null,
                    latitude: place.geometry.location.lat(),
                    longitude: place.geometry.location.lng(),
                    aptNum: this.newAddress.aptNum,
                    note: this.newAddress.note
                };
            } else {
                this.msg = ERROR_MSG.ADDRESS.NOT_STREET_ADDRESS;
                this.resetAddress(this.newAddress.cmd);
            }
        },
        deleteAddress(addressId) {
            this.$modal.show("dialog", {
                text: "Are you sure you want to delete this address?",
                buttons: [
                    {
                        title: "Yes",
                        handler: () => {
                            const apiReq = {
                                cb: this.customer.id,
                                cmd: API_CMDS.ADDRESS.DELETE,
                                id: addressId
							};
							const addressEventLabel = this.getAddressEventLabel();
                            customerService
                                .deleteAddress(apiReq)
                                .then(res => {
                                    if (res.data.body.msg === "Address removed") {
										this.$tpa.trackUserAddressRemove(addressEventLabel);
                                        // set delivery address to null if same as deleted address
                                        if (this.deliveryAddress && this.deliveryAddress.id === apiReq.id) {
                                            this.setDeliveryAddress(null);
                                        }
										this.deleteUsersAddress(apiReq);
										this.addAddressFlag = false;
										this.fullName = "";
										this.msg = SUCCESS_MSG.ADDRESS.DELETED
										this.$modal.hide("dialog");
									} else {
										this.$tpa.trackUserAddressRemoveFailure(addressEventLabel);
                                        this.$emit("error", ERROR_MSG.ADDRESS.DELETE);
                                    }
                                    this.$modal.hide("dialog");
                                })

                                .catch(err => {
                                    this.msg = err.data.message;
                                    this.$modal.hide("dialog");
                                });
                        }
                    },
                    {
                        title: "No"
                    }
                ]
            });
		},
		getStreetNumber(address) {
			const streetNumber = address.addressComponents.streetNumber.shortName || '';
			return streetNumber;
		},
		getStreetName(address) {
			const streetName = address.addressComponents.streetName.shortName || '';
			return streetName;
		},
        toggleAddressForm(cmd, address) {
            this.newAddress.cmd = cmd;
            this.msg = "";
            if (cmd === API_CMDS.ADDRESS.ADD) {
                this.newAddress.id = "";
                this.newAddress.address = "";
                this.newAddress.aptNum = ""
                this.newAddress.note = "";
                this.newAddress.addressComponents = {};
                this.addAddressFlag = !this.addAddressFlag; 
            } else if (cmd === API_CMDS.ADDRESS.EDIT) {
                this.addAddressFlag  = true;
                this.newAddress.id = address.id;
                this.newAddress.address = address.formattedAddress;
                this.newAddress.aptNum = address.aptNum;
                this.newAddress.note = address.note;
                this.newAddress.addressComponents = address.addressComponents;
            }
		},
		updateAutoCompleteText() {
			// update autocomplete in safari (odd behavior using shadow DOM inside a modal)
			const gmapInput = this.$el.querySelector('.gmap-autocomplete input');
			gmapInput.style.transform = 'translateZ(0)';
			EventBus.$emit('reset-error-message', 'reset');
		},
		logoutAction() {
			this.resetAddress();
			this.setDeliveryAddress(null);
		},
	},
    mounted() {
		const customer = this.customer;
		if (customer.addresses.length === 0) {
			return;
		}
        // only use client side checking if location doesnt do region checking
        if (this.customer.addresses.length && !this.location.delivery.hasDeliveryRegionsSet) {
            let lastUsedAddress = null;
            this.updateCustomer({
                addresses: this.customer.addresses.map(address => {
                    address.delivery.isDeliverable = addressService.isWithinRangeOfLocation(address, this.location);

                    if (!this.deliveryAddress && address.delivery.isDeliverable && (!lastUsedAddress || lastUsedAddress.lastUsedDate < address.lastUsedDate )) {
                        lastUsedAddress = address;
                    }
                    return address;
                })
            });

            if (lastUsedAddress) {
                this.setAddress(lastUsedAddress);
            }

        } else if (this.customer.addresses.length) {
            if (!this.deliveryAddress) {
                const lastUsedAddress = addressService.getLastUsedAddress(this.customer.addresses);
                
                if (lastUsedAddress) {
                    this.setAddress(lastUsedAddress);
                }
            }
		}
	},
	created() {
		EventBus.$on('logout', this.logoutAction);
	},
	destroyed() {
		EventBus.$off('logout');
	},
};
</script>

<style lang="scss">
    @import "globalStyles";

    .gmap-autocomplete {
		padding: 0em 0em 1em;
		.address-label {
			display: none;
		}
    }

    input {
        font-family: $font-family;
	}

    #apt {
		margin-bottom: 1em;
		
		.column:last-of-type {
			margin-left: 0.5em;
		}
	}
	.text-input {
		z-index: $zTopInteractionLayer;
	}

    @include mobile {
        .checkout-choice.disabled-label.address {
            padding-left: 3px;
            margin-left: -3px;
        }
    }

    @include desktop {
        .checkout-choice.disabled-label.address {
            padding-left: 6px;
            margin-left: -6px;
        }
    }

</style>



// WEBPACK FOOTER //
// src/components/checkout/AddressChooser.vue